import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  User, 
  UserCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  updatePassword as firebaseUpdatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUserProfile, updateLastLogin, getUserProfile, updateUserProfile as updateUserData } from '../services/userService';
import { UserProfile } from '../types/user';
import { db, doc, getDoc, setDoc } from '../config/firebase';
import { 
  checkRateLimit, 
  recordFailedAttempt, 
  recordSuccess, 
  recordApiRequest,
  getClientIP,
  isValidEmail,
  validatePasswordStrength 
} from '../services/securityService';

// Define the Auth context types
interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  updateUserDisplayName: (displayName: string) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateUserProfile: (data: {firstName?: string, lastName?: string, photoURL?: string}) => Promise<void>;
  refreshUserData: () => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  isAuthenticated: boolean;
}

// Create the Auth context
const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  isAdmin: false,
  login: async () => { throw new Error('Not implemented'); },
  register: async () => { throw new Error('Not implemented'); },
  logout: async () => { throw new Error('Not implemented'); },
  resetPassword: async () => { throw new Error('Not implemented'); },
  signInWithGoogle: async () => { throw new Error('Not implemented'); },
  updateUserDisplayName: async () => { throw new Error('Not implemented'); },
  updatePassword: async () => { throw new Error('Not implemented'); },
  updateUserProfile: async () => { throw new Error('Not implemented'); },
  refreshUserData: async () => { throw new Error('Not implemented'); },
  signup: async () => { throw new Error('Not implemented'); },
  isAuthenticated: false,
});

// Export a hook to use the Auth context
export const useAuth = () => useContext(AuthContext);

// Auth Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Consolidated auth state to reduce re-renders
  const [authState, setAuthState] = useState({
    user: null as User | null,
    userProfile: null as UserProfile | null,
    loading: true,
    isAdmin: false,
    authError: null as string | null,
    isMobile: false,
    authInitialized: false
  });

  // Simplified mobile detection - no loading delays
  useEffect(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    setAuthState(prev => ({ ...prev, isMobile: isMobileDevice }));
  }, []); // Run only once

  // Optimized user profile loading
  const loadUserProfile = useCallback(async (currentUser: User | null) => {
    if (!currentUser) {
      setAuthState(prev => ({ ...prev, userProfile: null }));
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserProfile;
        setAuthState(prev => ({ 
          ...prev, 
          userProfile: userData,
          isAdmin: checkIfAdmin(currentUser.email, userData)
        }));
      } else {
        setAuthState(prev => ({ ...prev, userProfile: null }));
      }
    } catch (error) {
      setAuthState(prev => ({ ...prev, userProfile: null }));
    }
  }, []);

  // Function to check if user is admin
  const checkIfAdmin = useCallback((email: string | null, profile: UserProfile | null) => {
    // Check if admin flag is set in user profile
    if (profile && (profile.isAdmin === true || profile.isSiteAdmin === true)) {
      return true;
    }
    
    // Fallback to email check for legacy support
    if (!email) return false;
    
    // List of admin emails
    const adminEmails = [
      'whm1919@hotmail.com',     // Primary admin
      // Add new admin emails below:
      // 'newadmin@example.com',
    ];
    return adminEmails.includes(email);
  }, []);

  // Optimized auth state initialization
  useEffect(() => {
    // Set up timeout to prevent indefinite loading
    const loadingTimeout = setTimeout(() => {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        authInitialized: true,
        authError: prev.isMobile ? null : 'Authentication initialization took too long. Please refresh the page.'
      }));
    }, authState.isMobile ? 6000 : 10000);
    
    const unsubscribe = auth.onAuthStateChanged(async (currentUser: User | null) => {
      clearTimeout(loadingTimeout);
      
      // Update user and clear errors
      setAuthState(prev => ({
        ...prev,
        user: currentUser,
        authError: null,
        authInitialized: true
      }));
      
      // Load profile for authenticated users
      if (currentUser) {
        await loadUserProfile(currentUser);
      }
      
      // Mark loading as complete
      setAuthState(prev => ({ ...prev, loading: false }));
    });
    
    return () => {
      unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, [loadUserProfile, authState.isMobile]);

  // Login function with rate limiting
  const login = async (email: string, password: string): Promise<UserCredential> => {
    // Validate email format
    if (!isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    // Check rate limit for email
    const emailRateLimit = checkRateLimit(email, 'email', 'login');
    if (!emailRateLimit.allowed) {
      throw new Error(emailRateLimit.reason || 'Too many login attempts. Please try again later.');
    }

    // Check rate limit for IP
    const clientIP = getClientIP();
    const ipRateLimit = checkRateLimit(clientIP, 'ip', 'login');
    if (!ipRateLimit.allowed) {
      throw new Error(ipRateLimit.reason || 'Too many login attempts from this location. Please try again later.');
    }

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Record successful login (reset counters)
      recordSuccess(email, 'email', 'login');
      recordSuccess(clientIP, 'ip', 'login');
      
      // Update last login
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      return result;
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('Login error:', firebaseError.code, firebaseError.message);
      
      // Record failed attempt
      if (firebaseError.code === 'auth/wrong-password' || 
          firebaseError.code === 'auth/user-not-found' ||
          firebaseError.code === 'auth/invalid-email') {
        recordFailedAttempt(email, 'email', 'login');
        recordFailedAttempt(clientIP, 'ip', 'login');
      }
      
      throw error;
    }
  };

  // Register function with rate limiting and password validation
  const register = async (email: string, password: string): Promise<UserCredential> => {
    // Validate email format
    if (!isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    // Validate password strength
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.valid) {
      throw new Error(`Weak password: ${passwordValidation.feedback.join(', ')}`);
    }

    // Check rate limit for email
    const emailRateLimit = checkRateLimit(email, 'email', 'register');
    if (!emailRateLimit.allowed) {
      throw new Error(emailRateLimit.reason || 'Too many registration attempts. Please try again later.');
    }

    // Check rate limit for IP
    const clientIP = getClientIP();
    const ipRateLimit = checkRateLimit(clientIP, 'ip', 'register');
    if (!ipRateLimit.allowed) {
      throw new Error(ipRateLimit.reason || 'Too many registration attempts from this location. Please try again later.');
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Record successful registration (reset counters)
      recordSuccess(email, 'email', 'register');
      recordSuccess(clientIP, 'ip', 'register');
      
      // Note: User profile creation will be handled by the component after registration
      // to include additional details like first/last name
      
      return result;
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('Registration error:', firebaseError.code, firebaseError.message);
      
      // Record failed attempt
      recordFailedAttempt(email, 'email', 'register');
      recordFailedAttempt(clientIP, 'ip', 'register');
      
      throw error;
    }
  };

  // Google Sign-in function with rate limiting
  const signInWithGoogle = async (): Promise<UserCredential> => {
    // Check rate limit for IP (Google sign-in)
    const clientIP = getClientIP();
    const ipRateLimit = checkRateLimit(clientIP, 'ip', 'login');
    if (!ipRateLimit.allowed) {
      throw new Error(ipRateLimit.reason || 'Too many sign-in attempts. Please try again later.');
    }

    console.log('🔐 AuthContext: Attempting Google login');
    try {
      const provider = new GoogleAuthProvider();
      // Add additional scopes if needed
      provider.addScope('email');
      provider.addScope('profile');
      
      // Set custom parameters for security
      provider.setCustomParameters({
        prompt: 'select_account', // Force account selection
        hd: '', // Optional: restrict to specific domain
      });
      
      console.log('🔐 AuthContext: Google provider configured, attempting popup');
      
      try {
        const result = await signInWithPopup(auth, provider);
        
        // Record successful login
        recordSuccess(clientIP, 'ip', 'login');
        console.log('🔐 Google sign-in successful:', {
          email: result.user.email,
          displayName: result.user.displayName,
          uid: result.user.uid,
          photoURL: result.user.photoURL,
          providerId: result.providerId
        });
        
        // Create/update user profile after Google sign in
        if (result.user) {
          const userRef = doc(db, 'users', result.user.uid);
          console.log('🔐 AuthContext: Checking if user profile exists');
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            console.log('🔐 AuthContext: Creating new user profile after Google sign-in');
            await setDoc(userRef, {
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isPremium: false
            });
            console.log('🔐 AuthContext: User profile created successfully');
          } else {
            console.log('🔐 AuthContext: User profile already exists, updating last login');
            await updateLastLogin(result.user.uid);
          }
        }
        
        return result;
      } catch (popupError: any) {
        console.error('🔐 AuthContext: Popup error during Google sign-in:', popupError.code, popupError.message);
        
        // Record failed attempt
        recordFailedAttempt(clientIP, 'ip', 'login');
        
        if (popupError.code === 'auth/popup-blocked') {
          console.log('🔐 AuthContext: Popup was blocked by browser, try using redirect method instead');
        }
        throw popupError;
      }
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('🔐 Google sign-in error:', firebaseError.code, firebaseError.message);
      
      // Record failed attempt
      recordFailedAttempt(clientIP, 'ip', 'login');
      
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    console.log('🔐 AuthContext: Logging out');
    try {
      await signOut(auth);
      setAuthState(prev => ({ ...prev, user: null, userProfile: null }));
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('Logout error:', firebaseError.code, firebaseError.message);
      throw error;
    }
  };

  // Reset password function with rate limiting
  const resetPassword = async (email: string) => {
    // Validate email format
    if (!isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    // Check rate limit for email (prevent email enumeration attacks)
    const emailRateLimit = checkRateLimit(email, 'email', 'password-reset');
    if (!emailRateLimit.allowed) {
      // Don't reveal if email exists - generic message
      throw new Error('If an account with that email exists, a password reset link has been sent. Please check your email and try again later if needed.');
    }

    // Check rate limit for IP
    const clientIP = getClientIP();
    const ipRateLimit = checkRateLimit(clientIP, 'ip', 'password-reset');
    if (!ipRateLimit.allowed) {
      throw new Error(ipRateLimit.reason || 'Too many password reset requests. Please try again later.');
    }

    console.log('🔐 AuthContext: Sending password reset for', email);
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent to:', email);
      
      // Record successful request (always record to prevent enumeration)
      // Don't reset on success to prevent abuse
      recordApiRequest(email, 'email');
      recordApiRequest(clientIP, 'ip');
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('Reset password error:', firebaseError.code, firebaseError.message);
      
      // Record failed attempt
      recordFailedAttempt(email, 'email', 'password-reset');
      recordFailedAttempt(clientIP, 'ip', 'password-reset');
      
      // Don't reveal if email exists (security best practice)
      if (firebaseError.code === 'auth/user-not-found') {
        // Return generic message to prevent email enumeration
        throw new Error('If an account with that email exists, a password reset link has been sent.');
      }
      
      throw error;
    }
  };

  // Update user display name
  const updateUserDisplayName = async (displayName: string) => {
    try {
      if (authState.user) {
        await updateProfile(authState.user, { displayName });
        // Force refresh the user
        setAuthState(prev => ({ 
          ...prev, 
          user: prev.user ? { ...prev.user, displayName } : null 
        }));
      }
    } catch (error: unknown) {
      console.error('Update profile error:', error);
      throw error;
    }
  };
  
  // Update password
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (authState.user && authState.user.email) {
        // Re-authenticate user first
        const credential = EmailAuthProvider.credential(authState.user.email, currentPassword);
        await reauthenticateWithCredential(authState.user, credential);
        
        // Then update password
        await firebaseUpdatePassword(authState.user, newPassword);
        console.log('Password updated successfully');
      } else {
        throw new Error('User not logged in or missing email');
      }
    } catch (error: unknown) {
      console.error('Update password error:', error);
      const firebaseError = error as { code?: string; message?: string };
      if (firebaseError.code === 'auth/wrong-password') {
        throw new Error('Current password is incorrect');
      } else if (firebaseError.code === 'auth/weak-password') {
        throw new Error('New password is too weak. It should be at least 6 characters');
      }
      throw error;
    }
  };
  
  // Update user profile data (first name, last name, photo URL)
  const updateUserProfile = async (data: {firstName?: string, lastName?: string, photoURL?: string}) => {
    try {
      if (authState.user) {
        // Check if profile exists
        if (!authState.userProfile) {
          // Create initial profile if it doesn't exist
          await createUserProfile(authState.user.uid, {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: authState.user.email || '',
            photoURL: data.photoURL !== undefined ? data.photoURL : (authState.user.photoURL || ''),
            createdAt: new Date().toISOString(),
          });
        } else {
          // Update existing Firestore profile
          await updateUserData(authState.user.uid, data);
        }

        // Update Firebase Auth profile if photoURL is provided
        if (data.photoURL !== undefined && authState.user) {
          await updateProfile(authState.user, { photoURL: data.photoURL });
        }
        
        // Update display name in Firebase Auth
        if (data.firstName || data.lastName) {
          const firstName = data.firstName || (authState.userProfile?.firstName || '');
          const lastName = data.lastName || (authState.userProfile?.lastName || '');
          const displayName = `${firstName} ${lastName}`.trim();
          
          if (displayName) {
            await updateUserDisplayName(displayName);
          }
        }
        
        // Update local state
        setAuthState(prev => ({
          ...prev,
          userProfile: prev.userProfile ? { ...prev.userProfile, ...data } : null
        }));
        
        console.log('User profile updated successfully');
      } else {
        throw new Error('User not logged in');
      }
    } catch (error: unknown) {
      console.error('Update user profile error:', error);
      throw error;
    }
  };

  // Function to refresh user data
  const refreshUserData = async (): Promise<void> => {
    if (authState.user) {
      try {
        const profile = await getUserProfile(authState.user.uid);
        setAuthState(prev => ({
          ...prev,
          userProfile: profile,
          isAdmin: checkIfAdmin(authState.user?.email || null, profile)
        }));
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        throw error;
      }
    }
  };

  // Signup function
  const signup = async (email: string, password: string, displayName: string): Promise<any> => {
    console.log('🔐 AuthContext: Creating account for', email);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile for new signup
      if (result.user) {
        console.log('🔐 AuthContext: Creating user profile after signup');
        const userRef = doc(db, 'users', result.user.uid);
        await setDoc(userRef, {
          email: email,
          displayName: displayName,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isPremium: false
        });
      }
      
      return result;
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      console.error('Registration error:', firebaseError.code, firebaseError.message);
      throw error;
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user: authState.user,
    userProfile: authState.userProfile,
    loading: authState.loading,
    isAdmin: authState.isAdmin,
    login,
    register,
    logout,
    resetPassword,
    signInWithGoogle,
    updateUserDisplayName,
    updatePassword,
    updateUserProfile,
    refreshUserData,
    signup,
    isAuthenticated: !!authState.user,
  }), [
    authState.user,
    authState.userProfile,
    authState.loading,
    authState.isAdmin,
    login,
    register,
    logout,
    resetPassword,
    signInWithGoogle,
    updateUserDisplayName,
    updatePassword,
    updateUserProfile,
    refreshUserData,
    signup
  ]);

  // Simplified loading - no fullscreen overlay for web
  if (authState.loading && !authState.authInitialized) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          width: '100vw',
          flexDirection: 'column',
          background: 'transparent', // Remove gradient background
          padding: '20px',
          // Remove fixed positioning that causes overlay
          position: 'relative',
          // Remove high z-index
          zIndex: 1,
        }}>
          {!authState.authError ? (
            <>
              <div style={{ 
                width: authState.isMobile ? '50px' : '60px', 
                height: authState.isMobile ? '50px' : '60px', 
                border: '4px solid rgba(255, 255, 255, 0.3)',
                borderTop: '4px solid #ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }} />
              <div style={{ 
                fontSize: authState.isMobile ? '20px' : '24px', 
                color: '#ffffff',
                fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                fontWeight: '600',
                marginBottom: '10px',
                textAlign: 'center'
              }}>
                Kivity
              </div>
              <div style={{ 
                fontSize: authState.isMobile ? '14px' : '16px', 
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                textAlign: 'center',
                maxWidth: authState.isMobile ? '280px' : '320px',
                lineHeight: '1.4'
              }}>
                {authState.isMobile ? 'Loading your family app...' : 'Setting up your family dashboard...'}
              </div>
              {/* Mobile-specific loading indicator */}
              {authState.isMobile && (
                <div style={{
                  marginTop: '20px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  textAlign: 'center'
                }}>
                  This may take a moment on mobile
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ 
                fontSize: authState.isMobile ? '20px' : '24px', 
                color: '#ffffff',
                fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                fontWeight: '600',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                Connection Issue
              </div>
              <div style={{ 
                fontSize: authState.isMobile ? '14px' : '16px', 
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                textAlign: 'center',
                marginBottom: '30px',
                maxWidth: authState.isMobile ? '280px' : '300px',
                lineHeight: '1.4'
              }}>
                {authState.authError}
              </div>
              <button 
                onClick={() => {
                  setAuthState(prev => ({ ...prev, authError: null, loading: false, authInitialized: true }));
                }}
                style={{
                  padding: authState.isMobile ? '10px 20px' : '12px 24px',
                  fontSize: authState.isMobile ? '14px' : '16px',
                  backgroundColor: '#ffffff',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '10px'
                }}
              >
                Continue Anyway
              </button>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  padding: authState.isMobile ? '8px 16px' : '10px 20px',
                  fontSize: authState.isMobile ? '12px' : '14px',
                  backgroundColor: 'transparent',
                  color: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '6px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, Inter, SF Pro, Poppins, sans-serif',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Refresh Page
              </button>
            </>
          )}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `
          }} />
        </div>
      </AuthContext.Provider>
    );
  }

  // Ensure app always renders after initialization
  return (
    <AuthContext.Provider value={value}>
      {authState.authInitialized || !authState.loading ? children : null}
    </AuthContext.Provider>
  );
}; 